import React, { useCallback, useRef, useState } from 'react';

import api from '../api';
import { Provider as name } from '../config';
import { formatData } from '../../providersHelpers';
import { useGet, useGetById, useGetSchema, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';

import Context from './context';

interface Props {
    children: React.ReactNode;
}

const CountriesProvider: React.FC<Props> = (props: Props) => {
    const { children } = props;
    const [data, setData] = useState({ ids: [], byId: {} });
    const [schema, setSchema] = useState({});
    const [selected, setSelected] = useState({});
    const [error, setError] = useState({});
    const fetchPromiseRef = useRef<Promise<any> | null>(null);
    const dataLoadedRef = useRef(false);

    const getById = useGetById(api, selected, setSelected, setError);

    const handleGetData = useCallback((res) => {
        setData(formatData(res));
        dataLoadedRef.current = true;
        fetchPromiseRef.current = null;
        return res;
    }, []);
    const baseGet = useGet(api, data, handleGetData, setError);

    // Wrapper to prevent duplicate fetches - returns cached data or in-flight promise
    const get = useCallback(
        (params?: any, options?: any, updateState?: boolean) => {
            // If data is already loaded, return it
            if (dataLoadedRef.current) {
                return Promise.resolve(data);
            }
            // If a fetch is in progress, return the existing promise
            if (fetchPromiseRef.current) {
                return fetchPromiseRef.current;
            }
            // Otherwise, start a new fetch
            fetchPromiseRef.current = baseGet(params, options, updateState);
            return fetchPromiseRef.current;
        },
        [baseGet, data]
    );

    const getSchema = useGetSchema(api, schema, setSchema, setError);

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected({});
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    return (
        <Context.Provider
            value={{
                name,
                get,
                getById,
                getSchema,
                schema,
                post,
                put,
                data,
                setData,
                selected,
                setSelected,
                remove,
                error,
                softDelete
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default CountriesProvider;
